import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { PaletteOptions } from '@mui/material/styles'

/**
 * 'system' follows the OS; 'auto' follows the sun at the home location (dark below 6°, the phone's
 * rule), falling back to the OS when no home location is set.
 */
export type ThemeMode = 'light' | 'dark' | 'system' | 'auto'

/** The order the header toggle cycles through, and the order Settings lists them. */
export const THEME_MODES: readonly ThemeMode[] = ['light', 'dark', 'system', 'auto']

/**
 * Serializable snapshot of the selected theme's colors, persisted so the app can paint the
 * right theme on first load instead of flashing the default while the server themes fetch.
 * Only the (plain-object) palette is stored — the static typography/breakpoints/components are
 * re-attached from base.theme when rebuilding.
 */
export interface ThemeCache {
  id: string
  light: PaletteOptions
  dark: PaletteOptions
}

interface UIState {
  // State
  sidebarOpen: boolean
  themeName: string // e.g., 'default', 'adventure', 'ocean'
  themeMode: ThemeMode // 'system' follows the OS prefers-color-scheme setting
  /** What 'system'/'auto' last resolved to, so index.html can pre-paint 'auto' without the sun maths. */
  lastEffectiveMode: 'light' | 'dark'
  themeCache: ThemeCache | null // colors of the selected theme, for a flash-free first paint
  language: string // e.g., 'en-US', 'es-ES'
  loading: boolean
  error: string | null

  // Actions
  toggleSidebar: () => void
  setSidebarOpen: (open: boolean) => void
  setTheme: (themeName: string) => void
  setThemeMode: (mode: ThemeMode) => void
  setLastEffectiveMode: (mode: 'light' | 'dark') => void
  toggleThemeMode: () => void
  setThemeCache: (cache: ThemeCache) => void
  setLanguage: (language: string) => void
  setLoading: (loading: boolean) => void
  setError: (error: string | null) => void
  clearError: () => void
}

export const useUIStore = create<UIState>()(
  persist(
    set => ({
      // Initial state
      sidebarOpen: true,
      themeName: 'adventure', // Default theme
      themeMode: 'system', // Default to OS preference
      lastEffectiveMode: 'light',
      themeCache: null, // Populated once the active theme's colors are known
      language: 'en-US', // Default to English
      loading: false,
      error: null,

      // Actions
      toggleSidebar: () => set(state => ({ sidebarOpen: !state.sidebarOpen })),
      setSidebarOpen: open => set({ sidebarOpen: open }),
      setTheme: themeName => set({ themeName }),
      setThemeMode: themeMode => set({ themeMode }),
      setLastEffectiveMode: lastEffectiveMode => set({ lastEffectiveMode }),
      setThemeCache: themeCache => set({ themeCache }),
      toggleThemeMode: () =>
        set(state => {
          const index = THEME_MODES.indexOf(state.themeMode)
          return { themeMode: THEME_MODES[(index + 1) % THEME_MODES.length] }
        }),
      setLanguage: language => set({ language }),
      setLoading: loading => set({ loading }),
      setError: error => set({ error }),
      clearError: () => set({ error: null }),
    }),
    {
      name: 'adventurecore-ui',
      partialize: state => ({
        themeName: state.themeName,
        themeMode: state.themeMode,
        lastEffectiveMode: state.lastEffectiveMode,
        themeCache: state.themeCache,
        language: state.language,
        sidebarOpen: state.sidebarOpen,
      }),
    }
  )
)
